import { useCallback, useEffect, useState } from "react";
import {
  CalendarClient, contactEmail, contactName, firstValue,
  type AddressBook, type ContactCard,
} from "../lib/calendar";
import { Icon } from "./Icon";

/** Deterministic avatar colour, matching the message list's scheme. */
const AVATAR_COLOURS = ["#4F46E5", "#0EA5E9", "#059669", "#B45309", "#DC2626", "#7C3AED", "#DB2777"];
function colourFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/[\s.@]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

/**
 * Contacts, using the design's .peoplewrap / .pgrid / .pcard.
 *
 * JSContact (RFC 9553) stores emails, phones, organizations and titles as
 * keyed maps rather than arrays, so every read goes through firstValue()
 * instead of indexing [0].
 */
export function People({
  client, onCompose,
}: {
  client: CalendarClient;
  onCompose: (email: string, name: string) => void;
}) {
  const [cards, setCards] = useState<ContactCard[]>([]);
  const [books, setBooks] = useState<AddressBook[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ fullName: "", email: "", phone: "", organization: "", title: "" });

  const load = useCallback(async () => {
    setError(null);
    try {
      const [list, bookList] = await Promise.all([client.contacts(), client.addressBooks()]);
      setCards(list);
      setBooks(bookList);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load contacts");
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    const book = books[0];
    if (!book || !draft.fullName.trim()) return;
    try {
      await client.createContact({ addressBookId: book.id, ...draft, fullName: draft.fullName.trim() });
      setDraft({ fullName: "", email: "", phone: "", organization: "", title: "" });
      setAdding(false);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the contact");
    }
  }

  async function remove(card: ContactCard) {
    try {
      await client.deleteContact(card.id);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the contact");
    }
  }

  const field = (key: keyof typeof draft, label: string, type = "text") => (
    <input
      value={draft[key]}
      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
      placeholder={label}
      aria-label={label}
      type={type}
      style={{
        width: "100%", height: 36, borderRadius: 9, background: "var(--pane2)",
        border: "1px solid var(--line)", color: "var(--tx)", font: "400 12.5px/1 Inter",
        padding: "0 11px", marginBottom: 8, outline: "none",
      }}
    />
  );

  return (
    <section className="peoplewrap" data-screen-label="People">
      <h2 style={{ display: "flex", alignItems: "center", gap: 10 }}>
        People
        <span style={{ font: "400 12px/1 Inter", color: "var(--tx3)" }}>{cards.length}</span>
        <button className="pill" style={{ marginLeft: "auto" }} onClick={() => setAdding((v) => !v)}>
          <Icon name={adding ? "x" : "pen-line"} />
          {adding ? "Cancel" : "New contact"}
        </button>
      </h2>

      {error && (
        <div role="alert" style={{ color: "var(--bad)", font: "400 12.5px/1.4 Inter", marginBottom: 14 }}>
          {error}
        </div>
      )}

      {adding && (
        <div className="pcard" style={{ alignItems: "stretch", textAlign: "left", marginBottom: 18, maxWidth: 380 }}>
          {field("fullName", "Full name")}
          {field("email", "Email", "email")}
          {field("phone", "Phone", "tel")}
          {field("organization", "Organisation")}
          {field("title", "Job title")}
          <button className="pill pri" onClick={() => void save()} disabled={!draft.fullName.trim()}>
            Save contact
          </button>
        </div>
      )}

      {cards.length === 0 && !adding ? (
        <div className="empty" style={{ height: 240 }}>
          <Icon name="users" />
          <p>No contacts yet. Add one and it will be here on every device.</p>
        </div>
      ) : (
        <div className="pgrid">
          {cards.map((card) => {
            const name = contactName(card);
            const email = contactEmail(card);
            const org = firstValue(card.organizations)?.name;
            const jobTitle = firstValue(card.titles)?.name;
            const phone = firstValue(card.phones)?.number;
            return (
              <div className="pcard" key={card.id}>
                <div className="av" style={{ background: colourFor(email ?? name) }}>{initials(name)}</div>
                <b>{name}</b>
                {jobTitle && <span>{jobTitle}{org ? ` · ${org}` : ""}</span>}
                {!jobTitle && org && <span>{org}</span>}
                {email && <span>{email}</span>}
                {phone && <span>{phone}</span>}
                <div className="act">
                  {email && (
                    <button className="pill" onClick={() => onCompose(email, name)} title={`Email ${name}`}>
                      <Icon name="mail" />Email
                    </button>
                  )}
                  <button className="pill" onClick={() => void remove(card)} title={`Delete ${name}`}>
                    <Icon name="trash-2" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
